import React, { useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useInactivityTimer } from '../hooks/useInactivityTimer';
import SessionTimeoutModal from '../components/session/SessionTimeoutModal';

/**
 * InactivityTimerProvider
 *
 * Global provider that manages user inactivity tracking and auto-logout.
 * Wraps the entire application to monitor user activity across all pages.
 *
 * Features:
 * - Tracks user activity (mouse, keyboard, touch, scroll)
 * - Shows warning modal at 25 minutes of inactivity
 * - Auto-logout at 30 minutes of inactivity
 * - Resets timer on any user interaction
 * - Clears authentication tokens on logout
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components to wrap
 */
const InactivityTimerProvider = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();

  /**
   * Check if user is currently logged in
   */
  const isAuthenticated = useCallback(() => {
    const token = localStorage.getItem('token');
    return !!token;
  }, []);

  /**
   * Handle user logout
   */
  const handleLogout = useCallback(() => {
    // Clear authentication tokens
    localStorage.removeItem('token');
    localStorage.removeItem('user');

    // Optional: Show toast notification
    // You can uncomment this if you have a toast notification system
    // toast.info('You were logged out due to inactivity');

    // Redirect to login page
    navigate('/login', {
      replace: true,
      state: { message: 'You were logged out due to inactivity.' }
    });
  }, [navigate]);

  /**
   * Handle warning modal display
   */
  const handleWarning = useCallback(() => {
    console.log('[InactivityTimer] Warning: User will be logged out soon');
  }, []);

  /**
   * Handle "Stay Logged In" button click
   */
  const handleStayLoggedIn = useCallback(() => {
    console.log('[InactivityTimer] User chose to stay logged in');
    resetTimer();
  }, []);

  /**
   * Handle "Log Out Now" button click
   */
  const handleLogoutNow = useCallback(() => {
    console.log('[InactivityTimer] User chose to logout immediately');
    handleLogout();
  }, [handleLogout]);

  // Initialize inactivity timer
  const {
    remainingTime,
    isWarning,
    resetTimer
  } = useInactivityTimer({
    warningTime: 25 * 60 * 1000, // 25 minutes
    logoutTime: 30 * 60 * 1000,  // 30 minutes
    onWarning: handleWarning,
    onLogout: handleLogout,
    enabled: isAuthenticated() // Only enable if user is logged in
  });

  /**
   * Reset timer when user logs in
   */
  useEffect(() => {
    if (isAuthenticated()) {
      resetTimer();
    }
  }, [location.pathname, isAuthenticated, resetTimer]);

  /**
   * Don't render timer on login page
   */
  if (!isAuthenticated() || location.pathname === '/login') {
    return <>{children}</>;
  }

  return (
    <>
      {children}

      {/* Session Timeout Warning Modal */}
      <SessionTimeoutModal
        isOpen={isWarning}
        remainingSeconds={remainingTime}
        onStayLoggedIn={handleStayLoggedIn}
        onLogoutNow={handleLogoutNow}
      />
    </>
  );
};

export default InactivityTimerProvider;
